import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Truck, Eye, EyeOff, Loader2 } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CATEGORIES = [
  "Host-Hostes Firması", "Ses - Işık Firması", "Catering", "Transfer",
  "Seyahat Acentası", "Baskıcı", "Atölye", "Kurulum Ekibi", "Otel-Konaklama",
  "Fuar Lojistik", "Promosyon", "Bez-Kağıt Çanta", "Plaket", "Tasarım Ajansı",
  "Güvenlik", "Mobilya", "Ambulans", "Montaj", "Event Ajansı"
];

export default function SupplierAuthPage() {
  const [mode, setMode] = useState("login");
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [form, setForm] = useState({ email: "", password: "", company_name: "", category: "" });
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (mode === "login") {
        const res = await axios.post(`${API}/supplier-auth/login`, {
          email: form.email,
          password: form.password,
        });
        localStorage.setItem("supplier_token", res.data.token);
        localStorage.setItem("supplier_slug", res.data.slug);
        toast.success("Giriş başarılı!");
        navigate("/tedarikci-panel");
      } else {
        if (!form.company_name) {
          toast.error("Firma adı gerekli");
          setLoading(false);
          return;
        }
        const res = await axios.post(`${API}/supplier-auth/register`, {
          email: form.email,
          password: form.password,
          company_name: form.company_name,
          category: form.category,
        });
        localStorage.setItem("supplier_token", res.data.token);
        localStorage.setItem("supplier_slug", res.data.slug);
        toast.success("Kayıt başarılı!");
        navigate("/tedarikci-panel");
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Bir hata oluştu");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-[80vh] flex items-center justify-center px-4 py-12">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <div className="w-16 h-16 bg-[#1B4F72]/10 rounded-2xl flex items-center justify-center mx-auto mb-4">
            <Truck className="w-8 h-8 text-[#1B4F72]" />
          </div>
          <h1 className="text-2xl font-bold text-stone-900 font-serif">Tedarikçi Portalı</h1>
          <p className="text-stone-500 text-sm mt-1">Hizmetlerinizi sergileyin, fuar sektörüne ulaşın</p>
        </div>

        <div className="bg-white rounded-xl border border-stone-200 p-6 shadow-sm">
          <div className="flex mb-6 bg-stone-100 rounded-lg p-1">
            <button
              onClick={() => setMode("login")}
              className={`flex-1 py-2 text-sm font-medium rounded-md transition-colors ${
                mode === "login" ? "bg-white shadow-sm text-stone-900" : "text-stone-500"
              }`}
            >
              GİRİŞ YAP
            </button>
            <button
              onClick={() => setMode("register")}
              className={`flex-1 py-2 text-sm font-medium rounded-md transition-colors ${
                mode === "register" ? "bg-white shadow-sm text-stone-900" : "text-stone-500"
              }`}
            >
              KAYIT OL
            </button>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            {mode === "register" && (
              <>
                <div>
                  <Label className="text-xs text-stone-500">FİRMA ADI</Label>
                  <Input
                    value={form.company_name}
                    onChange={(e) => setForm({ ...form, company_name: e.target.value })}
                    placeholder="Firma Adınız"
                    className="mt-1"
                    required
                    data-testid="supplier-name"
                  />
                </div>
                <div>
                  <Label className="text-xs text-stone-500">KATEGORİ</Label>
                  <select
                    value={form.category}
                    onChange={(e) => setForm({ ...form, category: e.target.value })}
                    className="mt-1 w-full h-10 rounded-md border border-stone-200 px-3 text-sm"
                    data-testid="supplier-category"
                  >
                    <option value="">Kategori Seçin</option>
                    {CATEGORIES.map((cat) => (
                      <option key={cat} value={cat}>{cat}</option>
                    ))}
                  </select>
                </div>
              </>
            )}

            <div>
              <Label className="text-xs text-stone-500">E-POSTA</Label>
              <Input
                type="email"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                placeholder="ornek@email.com"
                className="mt-1"
                required
                data-testid="supplier-email"
              />
            </div>

            <div>
              <Label className="text-xs text-stone-500">ŞİFRE</Label>
              <div className="relative mt-1">
                <Input
                  type={showPassword ? "text" : "password"}
                  value={form.password}
                  onChange={(e) => setForm({ ...form, password: e.target.value })}
                  placeholder="••••••••"
                  required
                  data-testid="supplier-password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-stone-400"
                >
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>

            <Button
              type="submit"
              disabled={loading}
              className="w-full bg-[#1B4F72] hover:bg-[#154360] h-11"
              data-testid="supplier-submit"
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : mode === "login" ? "GİRİŞ YAP" : "KAYIT OL"}
            </Button>
          </form>

          <div className="mt-6 pt-4 border-t border-stone-100 text-center text-xs text-stone-400">
            <Link to="/" className="hover:text-[#1B4F72]">Ana Sayfaya Dön</Link>
            <span className="mx-2">·</span>
            <Link to="/tedarikciler" className="hover:text-[#1B4F72]">Tüm Tedarikçiler</Link>
          </div>
        </div>
      </div>
    </div>
  );
}
